import { useEffect, useRef, useState } from 'react';
import { useVisualizerStore } from '@/stores/useVisualizerStore';
import { isEmbedded } from '@/utils/config';

const IS_EMBED = isEmbedded();

const STEPS = [
  { key: 'uploading', label: 'Uploading photo' },
  { key: 'submitting', label: 'Sending to TubVision' },
  { key: 'processing', label: 'TubVision is crafting your preview' },
  { key: 'finishing', label: 'Finalizing result' },
] as const;

// Soft ceilings: how far the tween is allowed to creep given the last real progress value.
// Prevents the bar from getting ahead of what's actually happened on the server.
function getSoftCeiling(realProgress: number): number {
  if (realProgress >= 1.0) return 1.0;
  if (realProgress >= 0.75) return 0.88;
  if (realProgress >= 0.50) return 0.72;
  if (realProgress >= 0.35) return 0.58;
  return 0.54; // 0.30 submitted — don't creep past halfway
}

const TWEEN_RATE = 0.0018; // per 100ms tick ≈ 1.8 %/sec — always moves, never races

export function ProcessingScreen() {
  const appState = useVisualizerStore((s) => s.appState);
  const progress = useVisualizerStore((s) => s.processingProgress);
  const storeMessage = useVisualizerStore((s) => s.processingMessage);
  const reset = useVisualizerStore((s) => s.reset);
  const uploadedImage = useVisualizerStore((s) => s.uploadedImage);
  const selectedFinish = useVisualizerStore((s) => s.selectedFinish);
  const selectedGloss = useVisualizerStore((s) => s.selectedGloss);

  // ── Typewriter ──────────────────────────────────────────────────────────────
  const [message, setMessage] = useState('');
  const [displayMessage, setDisplayMessage] = useState('');
  const messageIndex = useRef(0);

  useEffect(() => {
    if (!message) return;
    messageIndex.current = 0;
    setDisplayMessage('');
    const interval = setInterval(() => {
      messageIndex.current++;
      setDisplayMessage(message.slice(0, messageIndex.current));
      if (messageIndex.current >= message.length) clearInterval(interval);
    }, 28);
    return () => clearInterval(interval);
  }, [message]);

  // Sync store message → local message (store messages override rotating ones outside PROCESSING_ON_SERVER)
  useEffect(() => {
    if (appState !== 'PROCESSING_ON_SERVER') {
      setMessage(storeMessage);
    }
  }, [storeMessage, appState]);

  // ── Rotating contextual messages ────────────────────────────────────────────
  const glossLabel: Record<string, string> = { matte: 'matte', satin: 'satin', high: 'high-gloss' };
  const rotatingMessages = [
    `TubVision is analyzing your bathtub contours...`,
    `Applying the ${selectedFinish.name} color palette...`,
    `Simulating ${glossLabel[selectedGloss] ?? selectedGloss} surface reflections...`,
    `Fine-tuning the lighting and shadows...`,
    `Rendering the final finish details...`,
    `Almost there...`,
  ];
  const messagePhaseRef = useRef(0);

  useEffect(() => {
    if (appState !== 'PROCESSING_ON_SERVER') return;
    // Kick off the first rotating message immediately
    messagePhaseRef.current = 0;
    setMessage(rotatingMessages[0]);

    const interval = setInterval(() => {
      messagePhaseRef.current = (messagePhaseRef.current + 1) % rotatingMessages.length;
      setMessage(rotatingMessages[messagePhaseRef.current]);
    }, 2800);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState]);

  // ── Simulated progress tween ────────────────────────────────────────────────
  const [displayProgress, setDisplayProgress] = useState(progress);

  useEffect(() => {
    // Snap immediately on completion or when not in server-processing state
    if (progress >= 1.0 || appState !== 'PROCESSING_ON_SERVER') {
      setDisplayProgress(progress);
      return;
    }

    const ceiling = getSoftCeiling(progress);

    // If real progress jumped ahead of our display (e.g. a poll came back higher), jump too
    setDisplayProgress((prev) => Math.max(prev, progress));

    const interval = setInterval(() => {
      setDisplayProgress((prev) => {
        if (prev >= ceiling) return prev; // already at ceiling, wait for next real event
        return Math.min(prev + TWEEN_RATE, ceiling);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [progress, appState]);

  // ── Step status ─────────────────────────────────────────────────────────────
  const getStepStatus = (index: number) => {
    const stateIndex =
      appState === 'MODEL_LOADING' || appState === 'SUBMITTING_JOB'
        ? 1
        : appState === 'PROCESSING_ON_SERVER'
          ? progress < 0.5
            ? 2
            : 3
          : -1;

    if (index < stateIndex) return 'complete';
    if (index === stateIndex) return 'active';
    return 'pending';
  };

  const isProcessing = appState === 'PROCESSING_ON_SERVER';
  const displayPercent = Math.round(displayProgress * 100);

  return (
    <div className={`flex flex-col items-center justify-center px-4 bg-gradient-radial-blue ${IS_EMBED ? 'min-h-full py-8' : 'h-full'}`}>
      <div className="w-full max-w-sm perspective-1000">

        {/* Bathtub photo with scan overlay */}
        {uploadedImage && (
          <div className="relative mb-5 overflow-hidden rounded-2xl shadow-3d-card preserve-3d">
            <img
              src={uploadedImage.sourceUrl}
              alt="Your bathroom"
              className="aspect-video w-full object-cover"
            />
            {/* Blue shimmer scan — visible while TubVision is processing */}
            {isProcessing && (
              <div className="absolute inset-0 pointer-events-none">
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-bss-blue/20 to-transparent animate-shimmer"
                  style={{ backgroundSize: '200% 100%', animationDuration: '2.2s' }}
                />
              </div>
            )}
          </div>
        )}

        {/* 3D Progress Bar */}
        <div className="mb-6">
          <div className="progress-3d mb-3 h-5 overflow-hidden rounded-full">
            <div
              className="progress-3d-fill h-full rounded-full transition-all duration-700 ease-out"
              style={{ width: `${displayPercent}%` }}
            />
          </div>
          <p className="text-center text-sm font-semibold text-slate-700 min-h-[1.25rem]">
            {displayMessage}
            <span className="inline-block w-0.5 h-4 ml-0.5 bg-bss-blue animate-pulse align-middle" />
          </p>
          <p className="text-center text-xs font-medium text-bss-blue mt-1">
            {displayPercent}%
          </p>
        </div>

        {/* Step cards */}
        <div className="space-y-3">
          {STEPS.map((step, i) => {
            const status = getStepStatus(i);
            return (
              <div
                key={step.key}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-500 preserve-3d animate-step-enter ${
                  status === 'active'
                    ? 'bg-blue-50/80 shadow-glow-blue-sm border border-blue-100'
                    : status === 'complete'
                      ? 'bg-emerald-50/80 border border-emerald-100'
                      : 'bg-slate-50/60 border border-slate-100'
                }`}
                style={{
                  transform: status === 'active' ? 'translateZ(8px)' : 'translateZ(0)',
                  animationDelay: `${i * 80}ms`,
                }}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                    status === 'complete'
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                      : status === 'active'
                        ? 'bg-bss-blue text-white shadow-lg shadow-blue-600/30 animate-pulse-glow'
                        : 'bg-slate-200 text-slate-400'
                  }`}
                >
                  {status === 'complete' ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                <span
                  className={`text-sm font-medium transition-colors ${
                    status === 'pending' ? 'text-slate-400' : 'text-slate-800'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-5 text-center">
          <p className="text-xs text-slate-400">
            TubVision Preview ·{' '}
            <a
              href="https://www.AgenticPNW.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-bss-blue hover:text-bss-blue-dark transition-colors"
            >
              AgenticPNW
            </a>
          </p>
        </div>

        <button
          onClick={reset}
          className="mt-6 w-full rounded-xl py-2.5 text-sm font-medium text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
